// =====================================================================
//  Liste des stratégies OTP d'un projet (onglet « OTP » de la fiche Projet)
//  ---------------------------------------------------------------------
//  Calqué sur TemplateListItem : une grille Kendo + toolbar « Ajouter ».
//  La sélection (ou la création) ouvre l'éditeur dans un ComponentPanel.
//  Données réelles via /otp-strategy (proxy ClientWrapper -> WEB_API).
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {RessourceSlot} from "../../Tools/Disposable/RessourceSlot";
import {ComponentPanel} from "../ComponentPanel.js";
import {OtpEditor} from "./OtpEditor.js";
import {OtpRead} from "../../Controller/Otp/OtpRead.js";
import {OtpCreate} from "../../Controller/Otp/OtpCreate.js";
import {
    IOtpStrategyModel,
    OtpAlphabet,
    OtpChannelMask,
} from "../../Model/Otp/OtpStrategyModel.js";
import {PackageImportDialog} from "../Package/PackageImportDialog.js";
import {PackageImporter} from "../../Tools/Package/PackageImporter.js";
import {NotificationHelper} from "../../Helper/NotificationHelper.js";
import {
    OnOtpStrategyAdd,
    OnOtpStrategyDelete,
    OnOtpStrategyUpdate,
} from "../../Delegates/OtpStrategyDelegate.js";

export class OtpListItem implements IDisposableLike {
    #jqContainer: JQuery | undefined;
    #jqGrid: JQuery | undefined;
    #jqGridContainer: JQuery | undefined;
    #jqMainContainer: JQuery | undefined;
    #kGrid: kendo.ui.Grid | undefined;
    #projetId: string | undefined;
    #selectedItem: kendo.data.ObservableObject | undefined;
    readonly #editorSlot = new RessourceSlot<ComponentPanel>();

    constructor(option: IOtpListItemOption) {
        this.#jqMainContainer = option.jqMainContainer;
        this.#projetId = option.projetId;
    }

    public Init = async () => {
        this.#buildHtml();
        await this.#buildGrid();
        await this.#initEvent();
    };

    #buildHtml = () => {
        this.#jqContainer = $("<div></div>")
            .css({
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
            })
            .appendTo(this.#jqMainContainer);

        this.#jqGridContainer = $("<div></div>")
            .css({flexGrow: 1, width: "100%"})
            .appendTo(this.#jqContainer);

        this.#jqGrid = $("<div></div>").appendTo(this.#jqGridContainer);
    };

    #buildGrid = async () => {
        this.#kGrid = this.#jqGrid.kendoGrid({
            toolbar: [
                {
                    type: "button",
                    text: "Ajouter",
                    icon: "plus",
                    click: async () => this.#addStrategy(),
                },
                {
                    type: "button",
                    text: "Importer",
                    icon: "upload",
                    click: async () => this.#importStrategy(),
                },
            ],
            dataSource: await this.#load(),
            size: "small",
            width: "100%",
            height: "100%",
            selectable: "row",
            columns: [
                {selectable: true, width: "50px"},
                {title: "Nom", field: "Nom"},
                {title: "Canal", template: (d: IOtpStrategyModel) => OtpChannelMask.Libelle(d.Channels)},
                {title: "Pattern", template: (d: IOtpStrategyModel) => OtpListItem.PatternApercu(d)},
            ],
            change: async (e: kendo.ui.GridChangeEvent) => {
                if (!e.sender.select().length) return;
                this.#selectedItem = e.sender.dataItem(e.sender.select());
                const model = this.#selectedItem.toJSON() as any as IOtpStrategyModel;
                await this.#openEditor(model);
            },
        }).data("kendoGrid");
    };

    static PatternApercu = (d: IOtpStrategyModel): string => {
        const alpha =
            d.PatternAlphabet === OtpAlphabet.Numeric ? "0-9"
            : d.PatternAlphabet === OtpAlphabet.Alphanumeric ? "A-Z0-9"
            : "A-Z0-9 (sans ambigus)";
        const group = d.PatternGroupSize && d.PatternGroupSize > 0
            ? `, groupe ${d.PatternGroupSize}${d.PatternGroupSeparator ? ` « ${d.PatternGroupSeparator} »` : ""}`
            : "";
        return `${d.PatternLength ?? "?"} car. [${alpha}]${group}`;
    };

    #addStrategy = async () => {
        // L'ajout de ligne et la sélection sont pris en charge par
        // l'abonnement à OnOtpStrategyAdd (cf. #initEvent), déclenché par
        // OtpCreate. La méthode se contente de demander la création.
        await OtpCreate.Create(this.#projetId!);
    };

    // Import d'un paquet contenant une section « otpStrategy » : crée le
    // template e-mail (et ses traductions) puis la stratégie qui l'utilise,
    // et ouvre l'éditeur de la stratégie.
    #importStrategy = async () => {
        const pkg = await PackageImportDialog.Open({
            title: "Importer une stratégie OTP",
            intro: "Crée une stratégie OTP et son template e-mail dans ce projet à partir d'un paquet " +
                "produit par un agent IA (skill easymail-templates) ou exporté d'une autre stratégie.",
            context: {mode: "create-otp", fkProjet: this.#projetId!},
            describe: PackageImporter.DescribeCreate,
            confirmText: "Créer",
        });
        if (!pkg)
            return;

        const result = await PackageImporter.Create(pkg, this.#projetId!);
        if (!result?.otpStrategy)
            return;

        // OtpUpsert a déclenché OnOtpStrategyUpdate (rechargement) ; on
        // recharge ici de façon attendue pour sélectionner la nouvelle ligne.
        await this.#reload();
        const item = this.#kGrid.dataSource.data()
            .find((x: any) => x.get("Primarykey") === result.otpStrategy.Primarykey);
        if (item) {
            this.#kGrid.select(this.#kGrid.tbody.find(`tr[data-uid='${item.uid}']`));
            this.#kGrid.trigger("change", {sender: this.#kGrid} as any);
        }
        NotificationHelper.Instance.success(`Stratégie OTP et template « ${result.pivot.Nom} » créés : vérifiez la prévisualisation du template.`);
    };

    #openEditor = async (model: IOtpStrategyModel) => {
        const editor = new OtpEditor({model});

        const panel = this.#editorSlot.Reset(
            () =>
                new ComponentPanel({
                    jqMainContainer: $("[circ-id='projet-tab-container']"),
                    titre: model.Nom,
                }),
        );
        await panel.Init();
        await panel.Open(editor);
        await panel.Maximize();
    };

    static readonly #eventKey = "OtpListItem";

    #initEvent = async () => {
        await OnOtpStrategyAdd.Clear(OtpListItem.#eventKey);
        await OnOtpStrategyUpdate.Clear(OtpListItem.#eventKey);
        await OnOtpStrategyDelete.Clear(OtpListItem.#eventKey);

        // Ajout : insère la ligne créée puis la sélectionne, ce qui ouvre
        // l'éditeur via le handler `change` de la grille.
        OnOtpStrategyAdd.Bind((model: IOtpStrategyModel) => {
            const item = this.#kGrid.dataSource.add(model);
            const node = this.#kGrid.tbody.find(`tr[data-uid='${item.uid}']`);
            this.#kGrid.select(node);
            this.#kGrid.trigger("change", {sender: this.#kGrid} as any);
        }, OtpListItem.#eventKey);

        OnOtpStrategyUpdate.Bind(() => {
            this.#reload().then();
        }, OtpListItem.#eventKey);

        OnOtpStrategyDelete.Bind((model: IOtpStrategyModel) => {
            if (
                this.#selectedItem &&
                this.#selectedItem.get("Primarykey") === model.Primarykey
            ) {
                this.#kGrid.dataSource.remove(this.#selectedItem);
                this.#selectedItem = undefined;
            }
            this.#editorSlot.Dispose();
        }, OtpListItem.#eventKey);
    };

    #load = async () => {
        return OtpRead.Read({AsNoTracking: true, FkProjet: this.#projetId});
    };

    #reload = async () => {
        const data = await this.#load();
        this.#kGrid.dataSource.data(data as any);
    };

    public Dispose = () => {
        OnOtpStrategyAdd.Clear(OtpListItem.#eventKey).then();
        OnOtpStrategyUpdate.Clear(OtpListItem.#eventKey).then();
        OnOtpStrategyDelete.Clear(OtpListItem.#eventKey).then();
        this.#editorSlot.Dispose();
        if (this.#kGrid) this.#kGrid.destroy();
        if (this.#jqMainContainer) this.#jqMainContainer.empty();
    };
}

export interface IOtpListItemOption {
    jqMainContainer: JQuery;
    projetId?: string;
}
